import React, { useState } from "react";
import { T, SERIF, STATUS, statusColor } from "../theme/tokens.js";
import { Panel, Btn, Chip, Kpi, Empty, Notice } from "../components/ui/index.jsx";
import RegisterTable from "../components/RegisterTable.jsx";
import VendorRollup from "../components/VendorRollup.jsx";
import DrillPanel from "../components/DrillPanel.jsx";
import ContractSettings from "../components/ContractSettings.jsx";
import { useRegister, useRegisters } from "../lib/useRegister.js";
import { REGISTERS } from "../lib/registers.js";
import { fmtMoney, fmtK } from "../lib/format.js";
import { term, termLower, termPluralLower } from "../lib/terms.js";
import {
  vendorRollup, highRisks, isOpenRisk, budgetPosture, permitKpis,
  upcomingWindow, invoiceSummary, isOverdue, scheduleVarianceDays, isComplete,
} from "../lib/calc.js";

/* Tabs only appear when the contract has a use for them. A contract with no
   site label has no per-site view; one with no permits register in play does
   not advertise one. */
function tabsFor(contract) {
  const tabs = [
    ["overview", "Overview"],
    /* The monthly report's Overall Project Status table is this register and
       nothing else, so it has to be somewhere a person can reach. */
    ["workstream_status", "Workstream status"],
    ["deliverables", "Deliverables"],
    ["risks", "Risks"],
    ["issues", "Issues"],
    ["actions", "Actions"],
    ["decisions", "Decisions"],
    ["tasks", "Tasks"],
    ["permits", "Permits"],
    ["rfis", "RFIs"],
    ["compliance_items", "Compliance"],
    ["resources", "Resources"],
    ["procurement", "Procurement"],
    ["vendors", "Vendors"],
  ];
  return tabs;
}

function Overview({ contract, onEditSettings }) {
  const [drill, setDrill] = useState(null);
  const { data } = useRegisters(
    ["deliverables", "risks", "actions", "invoices", "expenses", "resources", "vendors",
     "permits", "milestones", "compliance_items"],
    contract.id,
  );

  if (!data) return <Empty text="Loading..." />;

  const rollup = vendorRollup(data.deliverables);
  const high = highRisks(data.risks, contract);
  const openRisks = data.risks.filter(isOpenRisk);
  const budget = budgetPosture(contract, data.expenses, data.resources);
  const permits = permitKpis(data.permits);
  const invoices = invoiceSummary(data.invoices, {
    vendors: data.vendors, contractTerms: contract.payment_terms_days,
  });
  const upcoming = upcomingWindow(
    { milestones: data.milestones, deliverables: data.deliverables }, 30);
  const drifting = data.deliverables.filter((d) => {
    const v = scheduleVarianceDays(d);
    return v !== null && v !== 0 && !isComplete(d);
  });
  const openActions = data.actions.filter((a) => a.status !== "Done");
  const overdueActions = openActions.filter(
    (a) => a.due_date && a.due_date < new Date().toISOString().slice(0, 10));

  const attention = [
    ...high.slice(0, 3).map((r) => ({
      color: T.risk, title: r.title, kind: "risks", row: r,
      detail: `Risk score ${r.risk_score ?? ""}, at or above this contract's line of ${contract.high_risk_threshold ?? 15}`,
    })),
    ...data.deliverables.filter((d) => isOverdue(d) && d.is_contract_deliverable)
      .slice(0, 3).map((d) => ({
        color: STATUS.overdue.ink, title: d.title, kind: "deliverables", row: d,
        detail: `Overdue contract deliverable${d.vendor ? `, ${d.vendor}` : ""}`,
      })),
    ...invoices.worst.slice(0, 2).map((p) => ({
      color: STATUS.inProgress.ink, title: `${p.invoice.title} ${p.invoice.vendor ? `(${p.invoice.vendor})` : ""}`,
      kind: "invoices", row: { ...p.invoice, _aging: p.aging },
      detail: `${p.aging.pastDays} days past Net ${p.aging.termsDays}, ${fmtMoney(p.invoice.amount)}`,
    })),
  ].slice(0, 6);

  /* The overview is the first screen anybody opens, and until now not one
     figure on it could be opened. Everything here counts something, so
     everything here opens what it counted. */
  const open = (kind, title, rows) => setDrill(rows?.length ? { kind, title, rows } : null);
  const openDeliverables = (title, rows) => open("deliverables", title, rows);
  const clickable = { cursor: "pointer" };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <DrillPanel drill={drill} onClose={() => setDrill(null)} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <div style={clickable}
          onClick={() => openDeliverables("All deliverables", data.deliverables)}>
          <Kpi label="Deliverables" value={rollup.total.total}
            sub={rollup.total.percentComplete !== null ? `${rollup.total.percentComplete}% complete` : "None yet"} />
        </div>
        <div style={clickable}
          onClick={() => openDeliverables("Overdue contract deliverables",
            rollup.total.records.overdueContractDeliverables)}>
          <Kpi label="Overdue CD" value={rollup.total.overdueContractDeliverables}
            sub="Contract deliverables"
            color={rollup.total.overdueContractDeliverables ? T.risk : T.navy} />
        </div>
        <div style={clickable} onClick={() => open("risks", "Open risks", openRisks)}>
          <Kpi label="Open risks" value={openRisks.length}
            sub={`${high.length} at or above ${contract.high_risk_threshold ?? 15}`}
            color={high.length ? T.risk : T.navy} />
        </div>
        <div style={clickable} onClick={() => open("invoices", "Invoices past terms",
          invoices.past.map((p) => ({ ...p.invoice, _aging: p.aging })))}>
          <Kpi label="Past terms" value={fmtK(invoices.pastTermsDollars)}
            sub={`${invoices.pastTermsCount} invoice${invoices.pastTermsCount === 1 ? "" : "s"}`}
            color={invoices.pastTermsCount ? T.risk : T.navy} />
        </div>
        <div style={clickable}
          onClick={() => open("expenses", "Expenses against the ceiling", data.expenses)}>
          <Kpi label="Budget used"
            value={budget.percentUsed === null ? "n/a" : `${budget.percentUsed}%`}
            sub={budget.ceiling ? `of ${fmtK(budget.ceiling)}` : "No ceiling set"}
            color={budget.color} />
        </div>
      </div>

      <div style={{ fontSize: 12.5, color: T.soft }}>
        Every figure on this page opens the records behind it. Click one.
      </div>

      {contract.summary && (
        <Panel title="Contract summary">
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65 }}>{contract.summary}</p>
        </Panel>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)", gap: 16 }}>
        <Panel title="Requiring attention" accent={T.gold}>
          {attention.length === 0
            ? <Empty text="Nothing flagged. Clean board." />
            : attention.map((a, i) => (
              <div key={i}
                onClick={a.row ? () => open(a.kind, a.title, [a.row]) : undefined}
                style={{ borderLeft: `3px solid ${a.color}`, paddingLeft: 10, marginBottom: 12,
                         cursor: a.row ? "pointer" : "default" }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>{a.title}</div>
                <div style={{ fontSize: 12, color: T.soft }}>{a.detail}</div>
              </div>
            ))}
        </Panel>

        <Panel title="Next 30 days" accent={T.gold}>
          {upcoming.length === 0 ? <Empty text="Nothing due." /> : upcoming.slice(0, 8).map((u, i) => (
            <div key={i}
              onClick={u.row ? () => open(
                u.kind === "milestone" ? "milestones" : "deliverables", u.title, [u.row]) : undefined}
              style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "flex-start",
                       cursor: u.row ? "pointer" : "default" }}>
              <div style={{ width: 8, height: 8, marginTop: 5, background: i % 2 ? T.navy : T.gold }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{u.title}</div>
                <div style={{ fontSize: 11.5, color: T.soft }}>
                  {u.date}{u.vendor ? ` · ${u.vendor}` : ""}
                </div>
              </div>
            </div>
          ))}
        </Panel>
      </div>

      {drifting.length > 0 && (
        <Panel title="Schedule drifting from contract" accent={T.risk}>
          <div style={{ fontSize: 13, color: T.soft, marginBottom: 10 }}>
            These have a schedule of record that disagrees with the contractual date.
            Positive means the schedule has slipped later than the contract allows.
            Click a row to open it.
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr>{["Deliverable", "Vendor", "Contract", "Schedule", "Drift"].map((h) => (
              <th key={h} style={{ textAlign: "left", padding: "6px 9px",
                borderBottom: `2px solid ${T.navy}`, color: T.navy, fontSize: 11,
                fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
            ))}</tr></thead>
            <tbody>{drifting.slice(0, 10).map((d) => {
              const v = scheduleVarianceDays(d);
              return (
                <tr key={d.id}
                  onClick={() => openDeliverables(d.title, [d])}
                  style={{ borderBottom: `1px solid ${T.line}`, cursor: "pointer" }}>
                  <td style={{ padding: "6px 9px" }}>{d.title}</td>
                  <td style={{ padding: "6px 9px" }}>{d.vendor}</td>
                  <td style={{ padding: "6px 9px" }}>{d.due_date}</td>
                  <td style={{ padding: "6px 9px" }}>{d.schedule_date}</td>
                  <td style={{ padding: "6px 9px", fontWeight: 700, color: v > 0 ? T.risk : T.ok }}>
                    {v > 0 ? `+${v}` : v} days
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </Panel>
      )}

      <VendorRollup deliverables={data.deliverables} onOpen={openDeliverables} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        <Panel title="Permits and approvals">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10 }}>
            {[["Total", permits.total, "total"], ["Open", permits.open, "open"],
              ["Over 30 days", permits.openOverThreshold, "openOverThreshold"],
              ["Avg days open", permits.averageDaysOpen ?? "n/a", "averageDaysOpen"]]
              .map(([l, v, bucket]) => {
              const rows = permits.records[bucket];
              return (
              <div key={l}
                onClick={rows?.length ? () => open("permits", `Permits: ${l.toLowerCase()}`, rows) : undefined}
                style={{ border: `1px solid ${T.line}`, borderRadius: 4, padding: 10,
                         cursor: rows?.length ? "pointer" : "default" }}>
                <div style={{ fontSize: 19, fontWeight: 700, color: T.ink }}>{v}</div>
                <div style={{ fontSize: 11, color: T.soft }}>{l}</div>
              </div>
              );
            })}
          </div>
        </Panel>
        <Panel title="Compliance">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.entries(
              data.compliance_items.reduce((acc, c) => {
                acc[c.status] = (acc[c.status] ?? 0) + 1; return acc;
              }, {})
            ).map(([status, n]) => (
              <span key={status} style={clickable}
                onClick={() => open("compliance_items", `Compliance: ${String(status).toLowerCase()}`,
                  data.compliance_items.filter((c) => c.status === status))}>
                <Chip color={statusColor(status)}>{n} {String(status).toLowerCase()}</Chip>
              </span>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="How this contract is set up" accent={T.gold}
        right={<Btn small kind="ghost" onClick={onEditSettings}>Edit settings</Btn>}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Chip color={T.navy}>{term(contract)}</Chip>
          {contract.region_label && <Chip color={T.steel}>{contract.region_label}</Chip>}
          {contract.site_label && <Chip color={T.steel}>{contract.site_label}</Chip>}
          <Chip color={T.steel}>High risk at {contract.high_risk_threshold ?? 15}</Chip>
          {contract.schedule_source && (
            <Chip color={T.soft}>
              {{ msproject: "Microsoft Project", p6: "Primavera P6", manual: "Kept by hand" }[contract.schedule_source]}
            </Chip>
          )}
          <Chip color={T.soft}>Net {contract.payment_terms_days ?? 60}</Chip>
        </div>
        <div style={{ fontSize: 12, color: T.soft, marginTop: 10 }}>
          {(contract.workstream_list ?? []).length
            ? `${contract.workstream_list.length} ${termPluralLower(contract)} defined.`
            : `No ${termLower(contract)} list yet, so that field is hidden on this contract's forms.`}
          {" "}
          {(contract.stage_list ?? []).length
            ? `${contract.stage_list.length} stages defined.`
            : "No stage list yet."}
        </div>
      </Panel>
    </div>
  );
}

export default function ContractWorkspace({ contract, onReloadContracts }) {
  const [tab, setTab] = useState("overview");
  const [settings, setSettings] = useState(false);
  const tabs = tabsFor(contract);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                    flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: T.navy, fontFamily: SERIF }}>{contract.name}</h2>
          <div style={{ fontSize: 13, color: T.soft, marginTop: 2 }}>
            {[contract.client,
              contract.contract_number && `Contract ${contract.contract_number}`,
              contract.contract_type,
              contract.start_date && `${contract.start_date} to ${contract.end_date || "TBD"}`,
            ].filter(Boolean).join(" · ")}
          </div>
        </div>
        <Btn small kind="ghost" onClick={() => setSettings(true)}>Settings</Btn>
      </div>

      <div style={{ display: "flex", gap: 2, marginTop: 14, borderBottom: `2px solid ${T.navy}`, overflowX: "auto" }}>
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: "8px 13px", border: "none", cursor: "pointer", fontSize: 13,
            background: tab === key ? T.navy : "none",
            color: tab === key ? "#fff" : T.soft,
            fontWeight: 700, whiteSpace: "nowrap", letterSpacing: "0.03em",
          }}>{label}</button>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === "overview"
          ? <Overview contract={contract} onEditSettings={() => setSettings(true)} />
          : (
            <Panel title={REGISTERS[tab].label}>
              <RegisterTable
                registerKey={tab}
                contract={contract}
                note={NOTES[tab]}
              />
            </Panel>
          )}
      </div>

      {settings && (
        <ContractSettings contract={contract} onClose={() => setSettings(false)}
          onSaved={onReloadContracts} />
      )}
    </div>
  );
}

const NOTES = {
  deliverables: "Status on file is what the vendor reported. Confirmed status is what the program stands behind, and every rollup reads that one.",
  risks: "Score is likelihood times impact, computed by the database. A register scoring 1 to 3 keeps its own numbers; the line for what counts as high is set per contract under Settings.",
  permits: "Turnaround is kept as written, so a range like 60-90 stays a range.",
  rfis: "An RFI is overdue against its response due date, which is a different thing from a permit simply sitting open.",
  vendors: "Payment terms here decide how an invoice ages when the invoice itself does not state its own.",
  procurement: "Contracts and purchase orders against scope. Not invoices and not expenses.",
};
